/**
 * <CodeEntityCombobox> — a searchable, code-aware picker for tenant org
 * entities (departments, job titles, branches, contract types).
 *
 * Why it exists: org entities were picked from plain `<Select>` dropdowns
 * labelled by name only, which (a) doesn't scale past a handful of rows, and
 * (b) NEVER surfaces the entity's code — yet codes are exactly what the CSV
 * importer needs. This picker filters CLIENT-SIDE by name OR code over a
 * pre-fetched list and renders `Name · CODE` so the code is always visible.
 *
 * Generic over any row with `{ id, nameAr, nameEn }` plus an optional `code`
 * and `status`. Modelled on <EmployeePicker> (Popover + search + listbox), but
 * list-driven instead of server-search-driven since org lists are small.
 *
 * Logical properties only; mirrors under RTL. Names render in the active
 * locale; the code renders LTR (`dir="ltr"`) so an alphanumeric code never
 * bidi-reorders inside an Arabic row.
 */
import { useEffect, useId, useMemo, useRef, useState, type JSX } from "react";
import { Check, ChevronDown, Search, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Icon } from "../../primitives/Icon/Icon";
import { Input } from "../../primitives/Input/Input";
import {
  PopoverContent,
  PopoverPortal,
  PopoverRoot,
  PopoverTrigger,
} from "../../primitives/Popover/Popover";
import { nonEmpty } from "../../types/NonEmptyString";

export interface CodeEntityRow {
  id: string;
  nameAr: string;
  nameEn: string;
  /** Stable machine code (e.g. `ENG-001`, `SWE-2`). Absent for entities with no code. */
  code?: string | null;
  /** `ACTIVE | ARCHIVED` — archived rows render dimmed. */
  status?: string | null;
}

export interface CodeEntityComboboxProps {
  items: CodeEntityRow[];
  /** Selected entity id, or "" for none. */
  value: string;
  onChange: (value: string) => void;
  /** Trigger label when nothing is selected. */
  placeholder: string;
  /** Accessible name for the trigger + listbox. */
  ariaLabel: string;
  /** Fires with the picked row (not on clear) — lets consumers read code/grade without re-finding. */
  onPick?: (row: CodeEntityRow) => void;
  /** Allow clearing the selection. @default true */
  clearable?: boolean;
  disabled?: boolean;
  /** Optional invalid state for form integration. */
  invalid?: boolean;
  id?: string;
  /** Id(s) of describing text (e.g. a FormField error) to associate. */
  ariaDescribedby?: string;
  /** What `<Field>` injects into its child; merged with `ariaDescribedby` / `invalid`. */
  "aria-describedby"?: string;
  "aria-invalid"?: boolean | "true" | "false";
}

function label(row: CodeEntityRow, isArabic: boolean): string {
  return isArabic ? row.nameAr || row.nameEn : row.nameEn || row.nameAr;
}

function matches(row: CodeEntityRow, q: string): boolean {
  const needle = q.toLowerCase();
  return (
    // A name can arrive null (Arabic-only org rows), whatever the row type says.
    (row.nameEn ?? "").toLowerCase().includes(needle) ||
    (row.nameAr ?? "").toLowerCase().includes(needle) ||
    (row.code ?? "").toLowerCase().includes(needle)
  );
}

export function CodeEntityCombobox({
  items,
  value,
  onChange,
  placeholder,
  ariaLabel,
  onPick,
  clearable = true,
  disabled = false,
  invalid = false,
  id,
  ariaDescribedby,
  "aria-describedby": fieldDescribedby,
  "aria-invalid": fieldInvalid,
}: CodeEntityComboboxProps): JSX.Element {
  const { t, i18n } = useTranslation("common");
  const isArabic = i18n.language.startsWith("ar");
  const [open, setOpen] = useState(false);
  const [rawQ, setRawQ] = useState("");
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const listboxId = useId();
  const isInvalid = invalid || fieldInvalid === true || fieldInvalid === "true";
  const describedBy =
    [...new Set(`${ariaDescribedby ?? ""} ${fieldDescribedby ?? ""}`.split(/\s+/))]
      .filter((x) => x !== "")
      .join(" ") || undefined;

  useEffect(() => {
    if (open) {
      const handle = window.setTimeout(() => searchInputRef.current?.focus(), 0);
      return () => window.clearTimeout(handle);
    }
    return undefined;
  }, [open]);

  const selected = useMemo(
    () => (value === "" ? null : (items.find((r) => r.id === value) ?? null)),
    [items, value],
  );

  const filtered = useMemo(() => {
    const q = rawQ.trim();
    return q === "" ? items : items.filter((r) => matches(r, q));
  }, [items, rawQ]);

  const triggerLabel =
    value === ""
      ? placeholder
      : selected !== null
        ? label(selected, isArabic)
        : `${t("entity_picker.unknown_selection")} (${value.slice(0, 8)}…)`;

  const handleSelect = (row: CodeEntityRow): void => {
    onChange(row.id);
    onPick?.(row);
    setOpen(false);
    setRawQ("");
  };

  return (
    <PopoverRoot
      open={open}
      onOpenChange={(o) => {
        if (disabled) return;
        setOpen(o);
        // Reopening starts on an empty search, not the one typed last time.
        if (!o) setRawQ("");
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          disabled={disabled}
          // Select-only combobox, as CountryCombobox: a button can't carry aria-invalid.
          role="combobox"
          // Include the current selection so screen readers announce the value,
          // not just the field name (a real <select> announces both).
          aria-label={selected !== null ? `${ariaLabel}: ${label(selected, isArabic)}` : ariaLabel}
          aria-describedby={describedBy}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-invalid={isInvalid || undefined}
          data-invalid={isInvalid || undefined}
          className={`flex w-full items-center justify-between gap-sm rounded-md border bg-surface px-sm py-xs min-h-10 text-start hover:border-strong focus-visible:focus-ring disabled:cursor-not-allowed disabled:opacity-60 ${
            isInvalid ? "border-danger" : "border-default"
          }`}
        >
          <span
            className={
              value === "" ? "text-body text-muted truncate" : "text-body text-default truncate"
            }
          >
            {triggerLabel}
          </span>
          <span className="flex items-center gap-xs shrink-0">
            {clearable && value !== "" && !disabled && (
              <span
                role="button"
                tabIndex={0}
                aria-label={nonEmpty(t("entity_picker.clear"))}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    onChange("");
                  }
                }}
                className="inline-flex items-center justify-center rounded-sm text-muted hover:text-default focus-visible:focus-ring"
              >
                <Icon as={X} size={14} aria-label="" />
              </span>
            )}
            <Icon as={ChevronDown} size={14} aria-label="" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverPortal>
        <PopoverContent
          align="start"
          sideOffset={4}
          className="w-[var(--radix-popover-trigger-width)] min-w-[260px] p-xs"
        >
          <div className="flex flex-col gap-xs">
            <div className="relative">
              <span className="pointer-events-none absolute inset-y-0 start-sm flex items-center text-muted">
                <Icon as={Search} size={14} aria-label="" />
              </span>
              <Input
                ref={searchInputRef}
                value={rawQ}
                onChange={(e) => setRawQ(e.target.value)}
                // Enter takes the first match, as typing a code and pressing Enter expects
                // (an option with focus takes Enter itself). Not while an IME is composing.
                onKeyDown={(e) => {
                  const first = filtered[0];
                  if (e.key !== "Enter" || e.nativeEvent.isComposing || first === undefined) return;
                  e.preventDefault();
                  handleSelect(first);
                }}
                placeholder={t("entity_picker.search_placeholder")}
                autoComplete="off"
                spellCheck={false}
                className="ps-xl"
                aria-label={nonEmpty(t("entity_picker.search_placeholder"))}
              />
            </div>

            <ul
              id={listboxId}
              role="listbox"
              aria-label={ariaLabel}
              className="max-h-72 overflow-y-auto flex flex-col"
            >
              {filtered.length === 0 ? (
                <li className="px-sm py-md text-label text-muted">{t("entity_picker.empty")}</li>
              ) : (
                filtered.map((row) => {
                  const isSelected = row.id === value;
                  const archived = row.status === "ARCHIVED";
                  return (
                    <li key={row.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        onClick={() => handleSelect(row)}
                        className="flex w-full items-center justify-between gap-sm rounded-sm px-sm py-xs text-start hover:bg-surface-sunken focus-visible:bg-surface-sunken focus-visible:outline-none"
                      >
                        <span className="flex flex-col min-w-0">
                          <span
                            className={`text-body truncate ${archived ? "text-muted" : "text-default"}`}
                          >
                            {label(row, isArabic)}
                          </span>
                          {(row.code ?? "") !== "" && (
                            <span
                              dir="ltr"
                              translate="no"
                              className="text-label text-muted truncate"
                            >
                              {row.code}
                            </span>
                          )}
                        </span>
                        {isSelected && (
                          <Icon
                            as={Check}
                            size={14}
                            aria-label={nonEmpty(t("entity_picker.selected"))}
                            className="text-primary shrink-0"
                          />
                        )}
                      </button>
                    </li>
                  );
                })
              )}
            </ul>
          </div>
        </PopoverContent>
      </PopoverPortal>
    </PopoverRoot>
  );
}
